import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  MenuItem,
  Select,
  TablePagination,
} from '@mui/material';
import { EmojiEvents, LocationOn, GridView, List } from '@mui/icons-material';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';

interface PostcodeRow {
  postcode_group: string;
  latitude: string | null;
  longitude: string | null;
  lead_count: number;
  total_value: number;
  customers: string;
}

interface Summary {
  totalLeads: number;
  totalValue: number;
  uniquePostcodes: number;
}

interface AnalyticsData {
  summary: Summary;
  postcodes: PostcodeRow[];
  groupBy: 'district' | 'full';
}

interface SourceOption {
  value: string;
  label: string;
  lead_count: number;
}

type GroupMode = 'district' | 'full';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
}

const MONTHS = ['All Months','January','February','March','April','May','June','July','August','September','October','November','December'];
const currentYear = new Date().getFullYear();
const YEARS = [0, ...Array.from({ length: 5 }, (_, i) => currentYear - i)];

export default function WonLeadsAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<GroupMode>('district');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [source, setSource] = useState('');
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPostcode, setSelectedPostcode] = useState<PostcodeRow | null>(null);
  const geocodeTriedRef = useRef(false);

  const fetchData = (groupMode: GroupMode, m: number, y: number, s: string) => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams({ groupBy: groupMode });
    if (m > 0) params.set('month', String(m));
    if (y > 0) params.set('year', String(y));
    if (s) params.set('source', s);
    return axios.get(`/api/analytics/won-leads-by-postcode?${params}`)
      .then(res => {
        if (res.data.success) {
          setData(res.data);
          return res.data as AnalyticsData;
        }
        setError('Failed to load data');
        return null;
      })
      .catch(() => { setError('Failed to load data'); return null; })
      .finally(() => setLoading(false));
  };

  // Reload the source list whenever the period changes: it only lists sources that
  // exist in that period, and its counts match the totals shown once one is picked.
  useEffect(() => {
    const params = new URLSearchParams();
    if (month > 0) params.set('month', String(month));
    if (year > 0) params.set('year', String(year));
    axios.get(`/api/analytics/won-lead-sources?${params}`)
      .then(res => {
        if (!res.data.success) return;
        const list: SourceOption[] = res.data.sources;
        setSources(list);
        // The chosen source may not exist in the new period — fall back to All Sources.
        setSource(prev => (prev && !list.some(o => o.value === prev) ? '' : prev));
      })
      .catch(() => { /* silent — filter just stays as "All Sources" */ });
  }, [month, year]);

  useEffect(() => {
    setPage(0);
    fetchData(mode, month, year, source).then((d) => {
      if (!d || geocodeTriedRef.current) return;
      const hasMissing = d.postcodes.some(p => !p.latitude || parseFloat(p.latitude) === 0);
      if (!hasMissing) return;
      geocodeTriedRef.current = true;
      axios.post('/api/analytics/geocode-missing-customers')
        .then(r => {
          if (r.data?.success && r.data.updated > 0) {
            fetchData(mode, month, year, source);
          }
        })
        .catch(() => { /* silent — map just stays as-is */ });
    });
  }, [mode, month, year, source]);

  const mappable = data?.postcodes.filter(p => p.latitude && p.longitude && parseFloat(p.latitude) !== 0) ?? [];

  const maxCount = mappable.length > 0 ? Math.max(...mappable.map(p => Number(p.lead_count))) : 1;

  const mapCenter: [number, number] = mappable.length > 0
    ? [
        mappable.reduce((s, p) => s + parseFloat(p.latitude!), 0) / mappable.length,
        mappable.reduce((s, p) => s + parseFloat(p.longitude!), 0) / mappable.length,
      ]
    : [53.65, -2.35];

  // Create a label-style DivIcon for each postcode area
  function makePostcodeIcon(postcode: string, count: number, max: number) {
    const ratio = max > 1 ? (count - 1) / (max - 1) : 1;
    // Dark navy -> lighter blue based on count
    const r = Math.round(44 + (52 - 44) * (1 - ratio));
    const g = Math.round(62 + (152 - 62) * (1 - ratio));
    const b = Math.round(80 + (219 - 80) * (1 - ratio));
    const bg = `rgb(${r},${g},${b})`;
    const html = `
      <div style="
        background:${bg};
        color:#fff;
        border-radius:6px;
        padding:3px 8px;
        font-size:12px;
        font-weight:700;
        white-space:nowrap;
        box-shadow:0 1px 4px rgba(0,0,0,0.3);
        border:1px solid rgba(255,255,255,0.25);
        cursor:pointer;
        line-height:1.4;
        pointer-events:auto;
      ">
        ${postcode}${mode === 'district' ? ` <span style="font-size:11px;opacity:0.9">(${count})</span>` : ''}
      </div>`;
    // Calculate width based on text length to avoid Leaflet's 12x12 default clipping
    const w = postcode.length * 9 + (mode === 'district' ? String(count).length * 8 + 28 : 16);
    const h = 26;
    return L.divIcon({ html, className: '', iconSize: [w, h], iconAnchor: [w / 2, h / 2] });
  }

  return (
    <Box>
      {/* Desktop layout: filters | stats | buttons on one row */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Select size="small" value={month} onChange={e => setMonth(Number(e.target.value))} sx={{ minWidth: 140 }}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={i}>{m}</MenuItem>)}
          </Select>
          <Select size="small" value={year} onChange={e => setYear(Number(e.target.value))} sx={{ minWidth: 110 }}>
            <MenuItem value={0}>All Years</MenuItem>
            {YEARS.slice(1).map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
          <Select size="small" value={source} onChange={e => setSource(String(e.target.value))} displayEmpty sx={{ minWidth: 160 }}>
            <MenuItem value="">All Sources</MenuItem>
            {sources.map(s => <MenuItem key={s.value} value={s.value}>{s.label} ({s.lead_count})</MenuItem>)}
          </Select>
        </Box>
        {data && (
          <Box display="flex" alignItems="center" gap={4}>
            <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: '#fff' }}>
              <EmojiEvents sx={{ color: 'primary.main' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.summary.totalLeads}</Typography>
                <Typography variant="caption" color="text.secondary">Won Leads</Typography>
              </Box>
            </Paper>
            <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: '#fff' }}>
              <LocationOn sx={{ color: 'secondary.main' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.summary.uniquePostcodes}</Typography>
                <Typography variant="caption" color="text.secondary">{mode === 'district' ? 'Postcode Areas' : 'Unique Postcodes'}</Typography>
              </Box>
            </Paper>
          </Box>
        )}
        <Box display="flex" gap={1}>
          <Button variant={mode === 'district' ? 'contained' : 'outlined'} startIcon={<GridView />} onClick={() => setMode('district')}>By Area</Button>
          <Button variant={mode === 'full' ? 'contained' : 'outlined'} startIcon={<List />} onClick={() => setMode('full')}>Full Postcode</Button>
        </Box>
      </Box>

      {/* Mobile layout: row 1 = filters, row 2 = buttons, row 3 = stats */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, mb: 2 }}>
        <Box display="flex" gap={1}>
          <Select size="small" value={month} onChange={e => setMonth(Number(e.target.value))} sx={{ flex: 1 }}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={i}>{m}</MenuItem>)}
          </Select>
          <Select size="small" value={year} onChange={e => setYear(Number(e.target.value))} sx={{ minWidth: 100 }}>
            <MenuItem value={0}>All Years</MenuItem>
            {YEARS.slice(1).map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </Box>
        <Select size="small" value={source} onChange={e => setSource(String(e.target.value))} displayEmpty fullWidth>
          <MenuItem value="">All Sources</MenuItem>
          {sources.map(s => <MenuItem key={s.value} value={s.value}>{s.label} ({s.lead_count})</MenuItem>)}
        </Select>
        <Box display="flex" gap={1}>
          <Button fullWidth variant={mode === 'district' ? 'contained' : 'outlined'} startIcon={<GridView />} onClick={() => setMode('district')}>By Area</Button>
          <Button fullWidth variant={mode === 'full' ? 'contained' : 'outlined'} startIcon={<List />} onClick={() => setMode('full')}>Full Postcode</Button>
        </Box>
        {data && (
          <Box display="flex" alignItems="center" gap={2}>
            <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: '#fff' }}>
              <EmojiEvents sx={{ color: 'primary.main' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.summary.totalLeads}</Typography>
                <Typography variant="caption" color="text.secondary">Won Leads</Typography>
              </Box>
            </Paper>
            <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, bgcolor: '#fff' }}>
              <LocationOn sx={{ color: 'secondary.main' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1}>{data.summary.uniquePostcodes}</Typography>
                <Typography variant="caption" color="text.secondary">{mode === 'district' ? 'Postcode Areas' : 'Unique Postcodes'}</Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Map */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ height: { xs: 300, sm: 400, md: 500 }, width: '100%', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {loading && (
              <Box position="absolute" top={0} left={0} right={0} bottom={0} display="flex" alignItems="center" justifyContent="center" zIndex={1000} bgcolor="rgba(255,255,255,0.7)">
                <CircularProgress />
              </Box>
            )}
            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
            {!error && (
              <MapContainer
                key={mode}
                center={mapCenter}
                zoom={mode === 'district' ? 9 : 10}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, HERE, Garmin, USGS, NGA'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                />
                {mappable.map((p) => {
                  const count = Number(p.lead_count);
                  return (
                    <Marker
                      key={p.postcode_group}
                      position={[parseFloat(p.latitude!), parseFloat(p.longitude!)]}
                      icon={makePostcodeIcon(p.postcode_group, count, maxCount)}
                      eventHandlers={{ click: () => setSelectedPostcode(p) }}
                    />
                  );
                })}
              </MapContainer>
            )}
            {selectedPostcode && (
              <Box sx={{
                position: 'absolute', bottom: 16, left: 16, zIndex: 1000,
                bgcolor: 'white', borderRadius: 2, boxShadow: 3, p: 2, minWidth: 180,
              }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography fontWeight={700} variant="body1">{selectedPostcode.postcode_group}</Typography>
                  <Typography sx={{ cursor: 'pointer', ml: 1, lineHeight: 1, color: 'text.secondary' }} onClick={() => setSelectedPostcode(null)}>✕</Typography>
                </Box>
                <Typography variant="body2">{Number(selectedPostcode.lead_count)} won lead{Number(selectedPostcode.lead_count) !== 1 ? 's' : ''}</Typography>
                {selectedPostcode.customers && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {selectedPostcode.customers}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      {data && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2}>
              {mode === 'district' ? 'Breakdown by Postcode Area' : 'Breakdown by Full Postcode'}
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell><strong>{mode === 'district' ? 'Area' : 'Postcode'}</strong></TableCell>
                    {mode === 'district' && <TableCell align="center"><strong>Won Leads</strong></TableCell>}
                    <TableCell><strong>Customers</strong></TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>On Map</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.postcodes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((p) => (
                    <TableRow key={p.postcode_group} hover>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">{p.postcode_group}</Typography>
                      </TableCell>
                      {mode === 'district' && (
                        <TableCell align="center">
                          <Chip
                            label={p.lead_count}
                            size="small"
                            sx={{ backgroundColor: 'primary.main', color: 'white', fontWeight: 700 }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: { xs: 160, sm: 260 } }}>
                          {p.customers}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {p.latitude && p.longitude ? (
                          <LocationOn sx={{ fontSize: 18, color: 'secondary.main' }} />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.postcodes.length}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
